<template>
  <div ref="hero" class="v2-hero" :class="[theme]">
    <div v-if="!videos.length" class="row">
      <div class="col-lg-8 col-xl-9 order-lg-1">
        <hero-video-placeholder />
      </div>
      <div class="col-lg-4 col-xl-3 order-lg-0">
        <hero-text-placeholder />
      </div>
    </div>

    <template v-else>
      <hero-item v-if="videos.length === 1" :id="`hero-0`" ref="video" :video="videos[0]" :cta-label="ctaLabel" />
      <app-carousel v-else class="hero-slider" :navigation-outer="true" :theme="theme" @page-change="pageChange">
        <slide v-for="(item, i) in videos" :key="i">
          <hero-item ref="video" :video="item" :cta-label="ctaLabel" />
        </slide>
      </app-carousel>
    </template>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, PropType, ref } from "vue";
import { Slide } from "vue3-carousel";
import AppCarousel from "@/components/v2/app-carousel/index.vue";
import { amplitudeCarouselTrack } from "@/utils/amplitude";
import HeroTextPlaceholder from "@/components/v2/placeholders/hero-text-placeholder/index.vue";
import HeroVideoPlaceholder from "@/components/v2/placeholders/hero-video-placeholder/index.vue";
import HeroItem from "@/components/hero-item/index.vue";
import { FreeareaVideo } from "@/interfaces/Video/FreeareaVideo.ts";

defineOptions({
  name: "hero",
});

const props = defineProps({
  videos: {
    type: Array as PropType<FreeareaVideo[]>,
    required: true,
  },
  ctaLabel: {
    type: String,
    required: false,
    default: null,
  },
  theme: {
    type: String,
    require: false,
    default: "light",
    validator: (value: string) => {
      return ["dark", "light"].includes(value);
    },
  },
});

const hero = ref<HTMLElement | null>(null);
const video = ref<(typeof HeroItem)[] | null>(null);
const resizeDelayTimer = ref<number | null>(null);

const setPaginationPosition = () => {
  nextTick(() => {
    const pagination = hero.value?.querySelector(".app-carousel-pagination");

    if (!pagination) {
      return;
    }

    if (!video.value || !props.videos.length) {
      return;
    }

    const el = video.value[0].$el;

    if (!el) {
      return;
    }

    const top = el.offsetHeight + 24;
    (pagination as HTMLElement).style.top = `${top}px`;
  });
};

const onResize = () => {
  clearTimeout(resizeDelayTimer.value as number);
  resizeDelayTimer.value = window.setTimeout(setPaginationPosition, 100);
};

const pageChange = () => {
  amplitudeCarouselTrack("Hero slider");
};

onMounted(() => {
  window.addEventListener("resize", onResize);

  nextTick(setPaginationPosition);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
});
</script>

<style lang="scss">
@import "./index.scss";
</style>
